import React from 'react';
import { Link } from 'react-router-dom';
import ImageLoader from '../components/ImageLoader';

const About: React.FC = () => {
  return (
    <div className="min-h-screen pt-20 pb-16">
      {/* Hero Section */}
      <section className="relative h-96">
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10">
          <h1 className="text-5xl md:text-6xl font-display font-bold text-white drop-shadow-lg">
            Our Story
          </h1>
        </div>
        <div className="absolute inset-0">
          <ImageLoader 
            index={9} 
            alt="Cambodian culture and heritage" 
            className="h-full w-full object-cover"
          />
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-display font-bold text-center text-tuktuk-brown mb-10">
              The Heart of Cambodian Cuisine
            </h2>
            <div className="flex flex-col md:flex-row gap-12 items-center mb-16">
              <div className="md:w-1/2">
                <ImageLoader 
                  index={1} 
                  alt="Founder of Tuk Tuk Restaurant" 
                  className="rounded-lg shadow-xl w-full h-auto"
                />
              </div>
              <div className="md:w-1/2">
                <h3 className="text-2xl font-display font-bold text-tuktuk-brown mb-4">
                  Born from Passion
                </h3>
                <p className="mb-4">
                  Born from a passion for authentic Khmer cuisine and a desire to share Cambodia's hidden gems, 
                  Tuk Tuk Restaurant is more than a dining establishment—it's a gateway to the soul of our nation.
                </p>
                <p>
                  Our founder envisioned a place where locals and travelers alike could gather to savor traditional 
                  dishes, embark on enlightening farm tours, and immerse themselves in the stories that make Cambodia unique.
                </p>
              </div>
            </div>

            <div className="bg-tuktuk-beige bg-opacity-20 p-8 rounded-lg shadow-lg mb-16">
              <h3 className="text-2xl font-display font-bold text-center text-tuktuk-brown mb-6">
                Our Philosophy
              </h3>
              <p className="text-lg text-center">
                "At Tuk Tuk Restaurant, we believe in the power of food to connect, educate, and inspire. 
                Our commitment to sustainability, community engagement, and preserving culinary heritage 
                drives everything we do. Every dish tells a story, every tour opens a door to discovery."
              </p>
            </div>

            <div className="mb-16">
              <h3 className="text-2xl font-display font-bold text-tuktuk-brown mb-6">
                Connecting with Cambodia
              </h3>
              <p className="mb-4">
                Nestled in the vibrant heart of Siem Reap, just a short journey from the magnificent 
                temples of Angkor Wat, our restaurant serves as a cultural bridge. We invite our guests to 
                experience Cambodia beyond the tourist attractions—through its flavors, traditions, and people.
              </p>
              <p>
                Our team members are all locals with deep roots in the community, eager to share their knowledge 
                and passion for Cambodian cuisine and culture. Through our farm tours and cooking classes, we offer 
                a hands-on approach to understanding the essence of what makes Cambodian food so special.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-tuktuk-beige bg-opacity-20">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-display font-bold text-center text-tuktuk-brown mb-12">
            Our Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-tuktuk-green rounded-full flex items-center justify-center text-white mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-display font-bold text-tuktuk-brown mb-3">
                Sustainability
              </h3>
              <p>
                We are committed to environmentally responsible practices, from our farm to our kitchen. 
                We minimize waste, use local products, and respect the natural cycles of the land.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-tuktuk-red rounded-full flex items-center justify-center text-white mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-display font-bold text-tuktuk-brown mb-3">
                Community
              </h3>
              <p>
                We support local farmers, artisans, and producers. Our restaurant is a hub for cultural 
                exchange, creating meaningful connections between visitors and the local community.
              </p>
            </div>
            <div className="bg-white p-8 rounded-lg shadow-lg text-center">
              <div className="w-16 h-16 bg-tuktuk-brown rounded-full flex items-center justify-center text-white mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-display font-bold text-tuktuk-brown mb-3">
                Authenticity
              </h3>
              <p>
                We honor traditional Cambodian recipes and cooking methods while allowing for creative 
                innovation that respects the essence of Khmer cuisine.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-display font-bold text-tuktuk-brown mb-6">
            Experience Tuk Tuk Restaurant
          </h2>
          <p className="text-lg mb-8 max-w-2xl mx-auto">
            We invite you to join us for a meal, a farm tour, or both. Come discover the heart and soul 
            of Cambodian cuisine and culture with us.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/menu" className="bg-tuktuk-red hover:bg-red-700 text-white font-bold py-3 px-8 rounded-lg transition duration-300">
              Explore Our Menu
            </Link>
            <Link to="/reservations" className="bg-tuktuk-green hover:bg-green-700 text-white font-bold py-3 px-8 rounded-lg transition duration-300">
              Make a Reservation
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About; 